/**
 * File Manager list / read / update / delete / search thunks — the "existing
 * file" operations. Create flows live in `files-save.ts`; conflict-resolution
 * helpers (name-exists check, overwrite confirm) live in `files-conflict.ts`.
 */

import getRouterConfig from "@hooks/useRouter";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  closeDock,
  updateMarketingItem,
  clearFormFields,
  enableSave,
  enableCreateButton,
} from "@actions/global";
import {
  loadFileLocations,
  loadFilesStart,
  loadFile,
  loadTrashFiles,
} from "@features/files/actions";
import { calculateOffsetLimit } from "@utils/pagination-helpers";
import { googleAnalyticsEventSelection } from "@components/shared/google-analytics-events";
import { getNowISO } from "@utils/time-helpers";
import { removeSetOfKeysFromObject } from "@utils/object-helpers";
import { getArrItemByKey } from "@utils/search-helpers";
import { createArrayFromInnerArrayOfObjects } from "@utils/array-helpers";
import { userErrorMessage } from "@utils/custom-errors";
import { invalidateBatchCall } from "@interfaces/batch-call";
import { BASE_PAGE } from "@constants/index";
import type { FilesConfig } from "@types";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  initialFilesConfig,
  getFileManagerAdapter,
  timeEnrichmentBeforeView,
  transformFileLocation,
  type Store,
} from "./files-helpers";
import type { FileThunk } from "./files-init";

/**
 * Fetches either the live file list or the trash list (determined by
 * `filesConfig.status`) and stores the result in `fileLocations`. Enables
 * the "Add New" button once the initial load completes.
 */
export const addFilesToRedux = (
  filesConfig: Partial<FilesConfig>
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(loadFilesStart());
  const { fileAllView, fileLocations } = store.getState();
  const live_status = filesConfig?.status === "live";
  const statusSet = live_status ? "file_locations" : "trash_files";
  const filesAdapter = await getFileManagerAdapter(store);
  const fileSet = live_status
    ? await filesAdapter.fetchAllFiles(
        calculateOffsetLimit(filesConfig, fileLocations.total)
      )
    : await filesAdapter.fetchTrashFiles(
        calculateOffsetLimit(
          { ...filesConfig, status: "trash" },
          fileLocations.trashTotal
        )
      );
  const updatedFileLocationList = transformFileLocation(
    fileSet[statusSet],
    fileSet?.file_manager_base_url || fileLocations.fileManagerBaseUrl,
    fileAllView.filesExtensionsToExclude
  );
  store.dispatch(
    live_status
      ? loadFileLocations(
          updatedFileLocationList,
          fileSet.total,
          fileLocations.trashedFiles,
          fileLocations.trashTotal,
          fileSet?.file_manager_base_url || fileLocations.fileManagerBaseUrl,
          fileSet?.ctid || fileLocations.ctid
        )
      : loadFileLocations(
          fileLocations.fileLocations,
          fileLocations.total,
          updatedFileLocationList,
          fileSet.total,
          fileSet?.file_manager_base_url || fileLocations.fileManagerBaseUrl,
          fileSet?.ctid || fileLocations.ctid
        )
  );
  store.dispatch(enableCreateButton(true));
};

/**
 * Loads a single file into `fileNewView`. When called for a live file,
 * also fetches asset versions so the versions dropdown is populated.
 */
export const setFile = (
  id: number | string,
  restoreFile: boolean = false
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());

  await runAsyncAction(dispatch, async () => {
    const storeContents = store.getState();
    const { file_id } = storeContents.fileNewView;

    const fileList = restoreFile
      ? createArrayFromInnerArrayOfObjects(
          storeContents.fileLocations.trashedFiles,
          "files"
        )
      : createArrayFromInnerArrayOfObjects(
          storeContents.fileLocations.fileLocations,
          "files"
        );
    const filesAdapter = await getFileManagerAdapter(store);

    let fileDetails =
      fileList.length !== 0
        ? getArrItemByKey("file_id", file_id || id, fileList)
        : await filesAdapter.fetchIndividualFile(id);
    fileDetails = fileDetails.hasOwnProperty("file")
      ? fileDetails.file
      : fileDetails;

    fileDetails = timeEnrichmentBeforeView(fileDetails);
    store.dispatch(loadFile(fileDetails));
    if (fileDetails.status === "live") {
      // Lazy import breaks the crud → versions module dependency cycle.
      const { loadFileVersions } = await import("./files-versions");
      const location = fileDetails?.multifile
        ? `${fileDetails?.file_location}/${fileDetails?.file_name}`
        : fileDetails?.file_location;
      await loadFileVersions(location)(dispatch, getState);
    }

    googleAnalyticsEventSelection("Load", {
      id: fileDetails.file_id,
      payload: fileDetails,
      category: "File Manager",
    });
  });
};

/**
 * Update / delete / clear-cache path for a single file. On success refreshes
 * the affected list slice (trash for delete, live for update/clear) and
 * redirects to `/files`.
 */
export const updateFile = (
  fileDetails: any,
  action: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());

  const deleteAction = action === "deleteFile";
  const clearCacheAction = action === "clearCache";
  const resolvedAction = deleteAction
    ? "delete"
    : clearCacheAction
      ? "clear"
      : "update";

  await runAsyncAction(
    dispatch,
    async () => {
      const { fileLocations, fileAllView, config } = store.getState();
      const filesAdapter = await getFileManagerAdapter(store);

      let file_location = null;
      if (!deleteAction) {
        file_location = await filesAdapter.fetchAllFiles(
          calculateOffsetLimit(
            {
              ...initialFilesConfig,
              status: "live",
              file_location: fileDetails.location,
            },
            fileLocations.total
          )
        );
      }
      const { multifile } = file_location
        ? file_location.file_locations[0]
        : false;
      const keyName = multifile
        ? "/".concat(fileDetails.file_location, "/", fileDetails.title)
        : "/".concat(fileDetails.file_location);
      const apiResult = deleteAction
        ? await filesAdapter.deleteFile(fileDetails.file_id)
        : clearCacheAction
          ? await filesAdapter.clearFileCache({ key_name: keyName })
          : await filesAdapter.patchFile(fileDetails.file_id, {
              ...fileDetails,
              date_modified: getNowISO(config.timeZone, "YYYY-MM-DDTHH:mm:ss"),
              multifile: multifile,
            });

      if (!(apiResult.success || apiResult.file)) throw apiResult;

      if (resolvedAction === "delete") {
        const trashSet = await filesAdapter.fetchTrashFiles(
          calculateOffsetLimit(
            { ...initialFilesConfig, status: "trash" },
            fileLocations.trashTotal
          )
        );
        const updatedTrashList = trashSet.success
          ? transformFileLocation(
              trashSet.trash_files,
              fileLocations.fileManagerBaseUrl,
              fileAllView.filesExtensionsToExclude
            )
          : [];
        store.dispatch(loadTrashFiles(updatedTrashList, trashSet.total));
      }
      const updatedFile = apiResult.file || fileDetails;
      const singleFileLocation = await filesAdapter.fetchAllFiles(
        calculateOffsetLimit(
          {
            ...initialFilesConfig,
            status: "live",
            file_location: updatedFile.location,
          },
          fileLocations.total
        )
      );
      let updatedFileLocation =
        singleFileLocation && singleFileLocation.file_locations
          ? transformFileLocation(
              singleFileLocation.file_locations,
              fileLocations.fileManagerBaseUrl,
              fileAllView.filesExtensionsToExclude
            )
          : [];
      updatedFileLocation = getArrItemByKey(
        "location_name",
        updatedFile.location,
        updatedFileLocation
      );
      store.dispatch(
        updateMarketingItem("file_location", updatedFileLocation, "update")
      );
      store.dispatch(clearFormFields());
      googleAnalyticsEventSelection(resolvedAction, {
        id: updatedFile.file_id,
        payload: removeSetOfKeysFromObject(updatedFile, ["file_data"]),
        category: "File Manager",
      });
    },
    {
      errorAction: resolvedAction,
      errorSubject: "file",
      buildErrorMessage: (err) =>
        userErrorMessage(resolvedAction, "file", (err as any) ?? ""),
      onSuccess: () => {
        // @ts-expect-error - history is possibly null; matches legacy typing
        const { history } = getRouterConfig();
        store.dispatch(
          setToastMessage(
            `File ${fileDetails.file_name}${
              clearCacheAction
                ? " cache has been cleared"
                : "has been " + resolvedAction + "d"
            }`,
            history ? history.push("/files") : null
          )
        );
      },
      onFailure: () => {
        store.dispatch(enableSave());
      },
    }
  );
};

export const deleteFileLocation = (
  fileLocationDetails: any,
  action: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(showSpinner());
  const { fileLocations, fileAllView } = store.getState();
  const deleteAction = action === "deleteFileLocation";
  const filesAdapter = await getFileManagerAdapter(store);

  const result = deleteAction
    ? await filesAdapter.deleteFileLocation(fileLocationDetails.location_name)
    : null;
  store.dispatch(closeDock());
  if (result && result.success) {
    const trashSet = await filesAdapter.fetchTrashFiles(
      calculateOffsetLimit(
        { ...initialFilesConfig, status: "trash" },
        fileLocations.trashTotal
      )
    );
    const updatedTrashList = trashSet.success
      ? transformFileLocation(
          trashSet.trash_files,
          fileLocations.fileManagerBaseUrl,
          fileAllView.filesExtensionsToExclude
        )
      : [];
    store.dispatch(loadTrashFiles(updatedTrashList, trashSet.total));
    store.dispatch(
      updateMarketingItem(
        "file_location",
        fileLocationDetails,
        deleteAction ? "delete" : "update"
      )
    );
    googleAnalyticsEventSelection("Delete", {
      id: fileLocationDetails.file_location_id,
      payload: fileLocationDetails,
      category: "File Manager",
    });
  } else {
    store.dispatch(
      reportError(
        userErrorMessage(
          "delete",
          `file URL ${fileLocationDetails.location_name}`,
          result
        )
      )
    );
  }
  store.dispatch(stopSpinner());
};

export const searchFilesByFilter = (
  status: string,
  searchValue: string,
  pageLimit: number,
  pageOrder: string,
  pageReversed: boolean,
  pageNumber: number = 1
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(loadFilesStart());
  const { fileLocations, fileAllView } = store.getState();
  const filesConfig = {
    status: status,
    search_all: searchValue,
    pageLimit: pageLimit,
    page_order: pageOrder,
    reverse: pageReversed,
    pageNumber: pageNumber,
  };
  invalidateBatchCall(BASE_PAGE.FILE, store);
  const live_status = status === "live";
  const fileSet = live_status ? "file_locations" : "trash_files";
  const total = live_status ? "total" : "trashTotal";
  const filesAdapter = await getFileManagerAdapter(store);

  const searchSet: any = live_status
    ? await filesAdapter.fetchAllFiles(
        calculateOffsetLimit(filesConfig, fileLocations[total])
      )
    : await filesAdapter.fetchTrashFiles(
        calculateOffsetLimit(filesConfig, fileLocations[total])
      );
  const updatedSearchSet = transformFileLocation(
    searchSet[fileSet],
    fileLocations.fileManagerBaseUrl,
    fileAllView.filesExtensionsToExclude
  );
  store.dispatch(
    live_status
      ? loadFileLocations(
          updatedSearchSet,
          searchSet[total],
          fileLocations.trashedFiles,
          fileLocations.trashTotal,
          fileLocations.fileManagerBaseUrl,
          fileLocations.ctid,
          searchValue
        )
      : loadFileLocations(
          fileLocations.fileLocations,
          fileLocations.total,
          updatedSearchSet,
          searchSet[total],
          fileLocations.fileManagerBaseUrl,
          fileLocations.ctid,
          searchValue
        )
  );
};
